/**
 * CL-DESIGN-BOARDS-DB-1A — Explicit Design Boards action authorization (pure).
 *
 * Manage does not substitute for contribute or comment. View is required for shared-board reads.
 */

import {
  DESIGN_BOARD_STATUS,
  designBoardEntitlesFileAccess,
  normalizeDesignBoardPermissions,
} from "./projectDesignBoardFoundationModel";

export const DESIGN_BOARD_ACTION = Object.freeze({
  VIEW_SHARED_BOARD: "view_shared_board",
  VIEW_DRAFT_OR_ARCHIVED_BOARD: "view_draft_or_archived_board",
  MANAGE_BOARD_METADATA: "manage_board_metadata",
  REORDER_PRESENTATION: "reorder_presentation",
  PUBLISH_OR_UNSHARE: "publish_or_unshare",
  ADD_ITEM_AS_CURATOR: "add_item_as_curator",
  EDIT_ANY_ITEM: "edit_any_item",
  DELETE_ANY_ITEM: "delete_any_item",
  ADD_OWN_ITEM: "add_own_item",
  EDIT_OWN_ITEM: "edit_own_item",
  DELETE_OWN_ITEM: "delete_own_item",
  POST_COMMENT: "post_comment",
  EDIT_OWN_COMMENT: "edit_own_comment",
  DELETE_OWN_COMMENT: "delete_own_comment",
  MODERATE_ANY_COMMENT: "moderate_any_comment",
});

function safeStr(value) {
  return String(value ?? "").trim();
}

export function isSharedClientVisibleBoard(board = {}) {
  return (
    safeStr(board.status) === DESIGN_BOARD_STATUS.SHARED
    && board.clientVisible === true
  );
}

export function isDraftBoard(board = {}) {
  return safeStr(board.status) === DESIGN_BOARD_STATUS.DRAFT;
}

export function isArchivedBoard(board = {}) {
  return safeStr(board.status) === DESIGN_BOARD_STATUS.ARCHIVED;
}

function actorOwnsRecord(record = {}, actorUid = "") {
  const uid = safeStr(actorUid);
  if (!uid) return false;
  return safeStr(record.createdByUid) === uid || safeStr(record.authorUid) === uid;
}

/**
 * @param {object} input
 * @param {string} input.action — DESIGN_BOARD_ACTION
 * @param {object} input.board
 * @param {object} input.permissions — assignment.permissions slice
 * @param {string} [input.actorUid]
 * @param {object} [input.targetRecord] — item or comment for ownership checks
 * @param {boolean} [input.isAdmin]
 */
export function evaluateDesignBoardAction({
  action = "",
  board = {},
  permissions = {},
  actorUid = "",
  targetRecord = null,
  isAdmin = false,
} = {}) {
  if (isAdmin) return { allowed: true, reason: "admin" };

  const perms = normalizeDesignBoardPermissions(permissions);
  const act = safeStr(action);

  const needsSharedContributor = [
    DESIGN_BOARD_ACTION.ADD_OWN_ITEM,
    DESIGN_BOARD_ACTION.EDIT_OWN_ITEM,
    DESIGN_BOARD_ACTION.DELETE_OWN_ITEM,
    DESIGN_BOARD_ACTION.POST_COMMENT,
    DESIGN_BOARD_ACTION.EDIT_OWN_COMMENT,
    DESIGN_BOARD_ACTION.DELETE_OWN_COMMENT,
  ].includes(act);

  if (needsSharedContributor && !isSharedClientVisibleBoard(board)) {
    return { allowed: false, reason: "board_not_shared" };
  }

  const curatorOnDraftOrShared = [
    DESIGN_BOARD_ACTION.MANAGE_BOARD_METADATA,
    DESIGN_BOARD_ACTION.REORDER_PRESENTATION,
    DESIGN_BOARD_ACTION.PUBLISH_OR_UNSHARE,
    DESIGN_BOARD_ACTION.ADD_ITEM_AS_CURATOR,
    DESIGN_BOARD_ACTION.EDIT_ANY_ITEM,
    DESIGN_BOARD_ACTION.DELETE_ANY_ITEM,
    DESIGN_BOARD_ACTION.MODERATE_ANY_COMMENT,
  ].includes(act);

  if (curatorOnDraftOrShared) {
    const draftOrArchived = isDraftBoard(board) || isArchivedBoard(board);
    const sharedOk = isSharedClientVisibleBoard(board);
    if (!draftOrArchived && !sharedOk) {
      return { allowed: false, reason: "board_state_blocked" };
    }
    if (sharedOk && !perms.canViewDesignBoards) {
      return { allowed: false, reason: "missing_canViewDesignBoards" };
    }
  }

  switch (act) {
    case DESIGN_BOARD_ACTION.VIEW_SHARED_BOARD:
      if (!perms.canViewDesignBoards) return { allowed: false, reason: "missing_canViewDesignBoards" };
      if (!isSharedClientVisibleBoard(board)) return { allowed: false, reason: "board_not_shared" };
      return { allowed: true, reason: "view_shared" };

    case DESIGN_BOARD_ACTION.VIEW_DRAFT_OR_ARCHIVED_BOARD:
      if (!perms.canManageDesignBoards) return { allowed: false, reason: "missing_canManageDesignBoards" };
      if (!isDraftBoard(board) && !isArchivedBoard(board)) {
        return { allowed: false, reason: "board_not_draft_or_archived" };
      }
      return { allowed: true, reason: "manage_draft_access" };

    case DESIGN_BOARD_ACTION.MANAGE_BOARD_METADATA:
    case DESIGN_BOARD_ACTION.REORDER_PRESENTATION:
    case DESIGN_BOARD_ACTION.PUBLISH_OR_UNSHARE:
    case DESIGN_BOARD_ACTION.ADD_ITEM_AS_CURATOR:
    case DESIGN_BOARD_ACTION.EDIT_ANY_ITEM:
    case DESIGN_BOARD_ACTION.DELETE_ANY_ITEM:
    case DESIGN_BOARD_ACTION.MODERATE_ANY_COMMENT:
      if (!perms.canManageDesignBoards) return { allowed: false, reason: "missing_canManageDesignBoards" };
      if (isSharedClientVisibleBoard(board) && !perms.canViewDesignBoards) {
        return { allowed: false, reason: "missing_canViewDesignBoards" };
      }
      return { allowed: true, reason: "manage" };

    case DESIGN_BOARD_ACTION.ADD_OWN_ITEM:
      if (!perms.canViewDesignBoards) return { allowed: false, reason: "missing_canViewDesignBoards" };
      if (!perms.canContributeDesignBoardItems) {
        return { allowed: false, reason: "missing_canContributeDesignBoardItems" };
      }
      return { allowed: true, reason: "contribute_add" };

    case DESIGN_BOARD_ACTION.EDIT_OWN_ITEM:
    case DESIGN_BOARD_ACTION.DELETE_OWN_ITEM:
      if (!perms.canViewDesignBoards) return { allowed: false, reason: "missing_canViewDesignBoards" };
      if (!perms.canContributeDesignBoardItems) {
        return { allowed: false, reason: "missing_canContributeDesignBoardItems" };
      }
      if (!actorOwnsRecord(targetRecord || {}, actorUid)) {
        return { allowed: false, reason: "not_owner" };
      }
      return { allowed: true, reason: "contribute_own" };

    case DESIGN_BOARD_ACTION.POST_COMMENT:
      if (!perms.canViewDesignBoards) return { allowed: false, reason: "missing_canViewDesignBoards" };
      if (!perms.canCommentOnDesignBoards) {
        return { allowed: false, reason: "missing_canCommentOnDesignBoards" };
      }
      return { allowed: true, reason: "comment" };

    case DESIGN_BOARD_ACTION.EDIT_OWN_COMMENT:
    case DESIGN_BOARD_ACTION.DELETE_OWN_COMMENT:
      if (!perms.canViewDesignBoards) return { allowed: false, reason: "missing_canViewDesignBoards" };
      if (!perms.canCommentOnDesignBoards) {
        return { allowed: false, reason: "missing_canCommentOnDesignBoards" };
      }
      if (!actorOwnsRecord(targetRecord || {}, actorUid)) {
        return { allowed: false, reason: "not_owner" };
      }
      return { allowed: true, reason: "comment_own" };

    default:
      return { allowed: false, reason: "unknown_action" };
  }
}

/**
 * Manager viewing a shared board still requires canViewDesignBoards (view flag explicit).
 */
export function canReadDesignBoardContent({ board = {}, permissions = {}, isAdmin = false } = {}) {
  if (isAdmin) return { allowed: true, reason: "admin" };
  if (isSharedClientVisibleBoard(board)) {
    return evaluateDesignBoardAction({
      action: DESIGN_BOARD_ACTION.VIEW_SHARED_BOARD,
      board,
      permissions,
    });
  }
  if (isDraftBoard(board) || isArchivedBoard(board)) {
    return evaluateDesignBoardAction({
      action: DESIGN_BOARD_ACTION.VIEW_DRAFT_OR_ARCHIVED_BOARD,
      board,
      permissions,
    });
  }
  return { allowed: false, reason: "board_state_blocked" };
}

export function designBoardActionRequiresFileEntitlementBeforeVisible(action = "") {
  const act = safeStr(action);
  return act === DESIGN_BOARD_ACTION.ADD_OWN_ITEM
    || act === DESIGN_BOARD_ACTION.ADD_ITEM_AS_CURATOR
    || act === DESIGN_BOARD_ACTION.PUBLISH_OR_UNSHARE;
}

export function boardFileEntitlementApplies(board = {}) {
  return designBoardEntitlesFileAccess(board);
}
